import { useCallback, useContext, useMemo } from 'react';
import UserContext from '~/context/common/UserContext';
import { sendSourcedFavoriteMessageToNative } from '~/native/common/dispatchers';
import { addFavorite } from '~/services/common';
import { logError } from '~/utilities/errorHandlers';
import type { FavoritesArguments, LifeCycleFn } from './types';
import useFavoritesFetcher from './useFavoritesFetcher';
import useLogInAndFavorite from './useLogInAndFavorite';

export type Options = {
  beforeAddFavorite?: LifeCycleFn;
  onAddFavorite?: LifeCycleFn;
};

const dummyFn = () => {};

function useAddFavorite({
  beforeAddFavorite = dummyFn,
  onAddFavorite = dummyFn
}: Options = {}) {
  const {
    favorites: { setData },
    triggerEvent
  } = useContext(UserContext);
  const fetchFavorites = useFavoritesFetcher();

  return useCallback(
    async (args: FavoritesArguments) => {
      const {
        subscribeToNotifications = false,
        vehicleId,
        nativeEventSource = ''
      } = args;

      try {
        beforeAddFavorite(args);

        const { data: vehicle } = await addFavorite({
          subscribeToNotifications,
          vehicleId
        });

        if (nativeEventSource)
          sendSourcedFavoriteMessageToNative(
            'add',
            {
              stockNumber: args.stockNumber ?? 0,
              vehicleId: args.vehicleId
            },
            nativeEventSource
          );

        if (vehicle) {
          setData(vehicles => vehicles.concat(vehicle));
        } else {
          await fetchFavorites();
        }

        triggerEvent.favoriteAdd();

        onAddFavorite(args);
      } catch (err) {
        logError(err, { location: 'Add favorites' });
      }
    },
    [beforeAddFavorite, fetchFavorites, onAddFavorite, setData, triggerEvent]
  );
}

export default function useAddFavoriteFlow(
  opts: Options
): (args: FavoritesArguments) => Promise<void> {
  const addFavoriteVehicle = useAddFavorite(opts);
  const loginAndAddFavorite = useLogInAndFavorite();

  return useMemo(
    () => loginAndAddFavorite(addFavoriteVehicle),
    [addFavoriteVehicle, loginAndAddFavorite]
  );
}
